import { useLookTexture } from '../../utils/useLookTexture';
import { useWorldStore } from '../../stores/worldStore';
import React, { useRef, useMemo, useEffect, Suspense } from 'react';
import { useFrame } from '@react-three/fiber';
import { useXR } from '@react-three/xr';
import { useGLTF, useAnimations } from '@react-three/drei';
import { Group, Quaternion, Euler, Bone, Box3 } from 'three';
import { readAvatarMeta, HOVER } from '../../avatar/avatarMeta';
import { vrLift } from './seatedMode';
import { clone } from 'three/examples/jsm/utils/SkeletonUtils.js';
import { LIBRARY_SOURCE_MODEL } from '../../config/emotes';
import { captureRest, fitLibraryClip, isTripoLibraryClip } from '../../avatar/libraryRetarget';
import { isVrmAvatar } from '../../avatar/vrm/vrmFormat';
import VrmVRBody from '../vrm/VrmVRBody';
import { repairHeadArmWeights } from '../../avatar/skinRepair';

/** Loads RPM avatar, hides head via bone scaling */
const RPMBody: React.FC<{ url: string; bodyRef: React.RefObject<Group | null> }> = ({ url, bodyRef }) => {
  const gltf = useGLTF(url);
  const headBoneRef = useRef<Bone | null>(null);

  // Idle animation so the body isn't a T-pose statue (visible in selfies
  // and to anyone looking down at their own body)
  const storedAnimations = useWorldStore(s => s.avatarAnimations);
  const meshyAnims = useMemo(() => {
    if (Object.keys(storedAnimations).length) return storedAnimations;
    try { return JSON.parse(localStorage.getItem('eb-avatar-animations') || 'null'); } catch { return null; }
  }, [url, storedAnimations]);
  const idleUrl = String(meshyAnims?.idle || '/animations/idle-male.glb');
  // Declared standing height / hover (avatar/avatarMeta.ts) — same hints the
  // desktop + remote renderer (FixedRetargetedAvatar) uses, so your headset
  // body is the size everyone else sees. Feet stay on the floor (the body is
  // never stretched to your real head height).
  const meta = useMemo(() => readAvatarMeta(meshyAnims), [meshyAnims]);
  const idleGltf = useGLTF(idleUrl);
  // In-app Tripo avatars idle with the shared library clip, made on another rig.
  const librarySkeleton = useGLTF(LIBRARY_SOURCE_MODEL);

  const avatarScene = useMemo(() => {
    repairHeadArmWeights(gltf.scene); // same Tripo weight repair as FixedRetargetedAvatar
    const cloned = clone(gltf.scene);

    // RPM unified mesh: find head-related bones in the skeleton
    cloned.traverse((child: any) => {
      if ((child.isSkinnedMesh || child.isMesh) && child.skeleton) {
        console.log('[VRAvatar] All bone names:', child.skeleton.bones.map((b: any) => b.name));
        for (const bone of child.skeleton.bones) {
          const name = bone.name.toLowerCase();
          // Match "Head" bone — but not "HeadTop_End" which is a tip marker
          if (name === 'head' || name === 'head_m' || name === 'mixamorighead') {
            headBoneRef.current = bone;
            console.log(`[VRAvatar] Found head bone: "${bone.name}"`);
            break;
          }
        }
        // If exact match failed, try includes as fallback
        if (!headBoneRef.current) {
          for (const bone of child.skeleton.bones) {
            const name = bone.name.toLowerCase();
            if (name.includes('head') && !name.includes('top') && !name.includes('end')) {
              headBoneRef.current = bone;
              console.log(`[VRAvatar] Found head bone (fallback): "${bone.name}"`);
              break;
            }
          }
        }
      }
    });

    return cloned;
  }, [gltf.scene]);

  const lookTextureUrl = useWorldStore(s => s.avatarLookTextureUrl);
  useLookTexture(avatarScene, lookTextureUrl);

  const idleClips = useMemo(() => {
    if (isTripoLibraryClip(idleUrl) && idleGltf.animations[0]) {
      const cl = fitLibraryClip(idleGltf.animations[0], librarySkeleton.scene, captureRest(gltf.scene));
      cl.name = 'idle';
      return [cl];
    }
    return idleGltf.animations.map((c) => {
      const cl = c.clone();
      cl.name = 'idle';
      return cl;
    });
  }, [idleGltf.animations, idleUrl, librarySkeleton.scene, gltf.scene]);
  const avatarObjRef = useRef(avatarScene);
  avatarObjRef.current = avatarScene;
  const { actions } = useAnimations(idleClips, avatarObjRef as any);
  useEffect(() => {
    const idle = actions.idle;
    if (idle) {
      idle.reset().play();
      idle.timeScale = 0.65; // matches the calmed idle speed elsewhere
    }
  }, [actions]);

  // Scale head bone to zero so it's invisible in first person
  useEffect(() => {
    if (headBoneRef.current) {
      headBoneRef.current.scale.set(0, 0, 0);
      // Expose for the selfie capture, which un-hides the head for one frame
      (window as any).__vrHeadBone = headBoneRef.current;
      console.log('[VRAvatar] Head bone scaled to 0');
    } else {
      console.warn('[VRAvatar] No head bone found — head will be visible');
    }
    return () => {
      if (headBoneRef.current) {
        headBoneRef.current.scale.set(1, 1, 1);
      }
      if ((window as any).__vrHeadBone === headBoneRef.current) {
        (window as any).__vrHeadBone = null;
      }
    };
  }, [avatarScene]);

  // Height fit, measured like FixedRetargetedAvatar: transformed bounds of the
  // model in bind pose (the cached gltf.scene — the mixer animates the clone),
  // scaled to __height, then feet re-grounded. Applied on a wrapper so the
  // idle clip can't overwrite it. Avatars without __height are left exactly
  // as before (scale 1, no offset).
  const fit = useMemo(() => {
    if (!meta.height) return { scale: 1, y: 0 };
    gltf.scene.updateMatrixWorld(true);
    const box = new Box3().setFromObject(gltf.scene);
    const h = box.max.y - box.min.y;
    if (!Number.isFinite(h) || h <= 0.01) return { scale: 1, y: 0 };
    const s = Math.abs(meta.height / h - 1) > 0.01 ? meta.height / h : 1;
    return { scale: s, y: Number.isFinite(box.min.y) ? -box.min.y * s : 0 };
  }, [gltf.scene, meta.height]);

  // Hover bots get the same gentle lift + bob others see. Only the body moves
  // (never the camera), so it adds no comfort cost.
  const fitRef = useRef<Group>(null);
  const hoverPhase = useRef(Math.random() * Math.PI * 2);
  const headShown = useRef(false);
  useFrame((state) => {
    if (!fitRef.current) return;
    let y = fit.y;
    if (meta.hover) {
      const t = performance.now() / 1000;
      y += HOVER.lift + HOVER.amp * Math.sin(t * Math.PI * 2 * HOVER.hz + hoverPhase.current);
    }
    fitRef.current.position.y = y;

    // Short avatars (a 1.1–1.3 m robot under a real-height headset): your eye
    // is well above the model's head, so the head can't block or swallow the
    // camera — keep it visible instead of leaving a headless stump (a robot's
    // head is ~40% of its height). Duck or crouch down to it and it hides
    // again, as it always does for full-size avatars. Eye above the feet is
    // the camera's rig-local height plus the seated-mode lift.
    const head = headBoneRef.current;
    if (meta.height && head) {
      const eye = state.camera.position.y + vrLift.applied;
      const top = meta.height + (meta.hover ? HOVER.lift + HOVER.amp : 0);
      headShown.current = eye > top + (headShown.current ? 0.08 : 0.15);
      head.scale.setScalar(headShown.current ? 1 : 0);
    }
  });

  return (
    <group ref={bodyRef} name="vr-avatar-body">
      <group ref={fitRef} name="vr-avatar-fit" scale={fit.scale} position={[0, fit.y, 0]}>
        <primitive object={avatarScene} />
      </group>
    </group>
  );
};

/** Simple colored body as fallback */
const FallbackBody: React.FC<{ bodyRef: React.RefObject<Group | null> }> = ({ bodyRef }) => (
  <group ref={bodyRef} name="vr-avatar-body">
    <mesh position={[0, 0.9, 0]}>
      <capsuleGeometry args={[0.18, 0.5, 8, 16]} />
      <meshStandardMaterial color="#2c5aa0" />
    </mesh>
    <mesh position={[-0.12, 0.2, 0]}>
      <capsuleGeometry args={[0.09, 0.45, 6, 8]} />
      <meshStandardMaterial color="#1a202c" />
    </mesh>
    <mesh position={[0.12, 0.2, 0]}>
      <capsuleGeometry args={[0.09, 0.45, 6, 8]} />
      <meshStandardMaterial color="#1a202c" />
    </mesh>
  </group>
);

interface VRAvatarBodyProps {
  avatarUrl?: string;
}

const VRAvatarBody: React.FC<VRAvatarBodyProps> = ({ avatarUrl }) => {
  const isPresenting = useXR((s) => !!s.session);
  const origin = useXR((s) => s.origin);
  const storeAnims = useWorldStore((s) => s.avatarAnimations) as Record<string, unknown> | null;
  const bodyRef = useRef<Group>(null);
  const smoothedYaw = useRef(0);
  const _worldQuat = useMemo(() => new Quaternion(), []);
  const _originQuat = useMemo(() => new Quaternion(), []);
  const _localQuat = useMemo(() => new Quaternion(), []);
  const _euler = useMemo(() => new Euler(), []);

  useFrame((state) => {
    if (!isPresenting || !bodyRef.current) return;

    const cam = state.camera;

    // 1. Compute local yaw first (subtract XROrigin rotation)
    cam.getWorldQuaternion(_worldQuat);
    if (origin) {
      origin.getWorldQuaternion(_originQuat);
      _localQuat.copy(_originQuat).invert().multiply(_worldQuat);
    } else {
      _localQuat.copy(_worldQuat);
    }
    _euler.setFromQuaternion(_localQuat, 'YXZ');
    const localHeadYaw = _euler.y;
    const targetYaw = localHeadYaw + Math.PI;

    // 2. Smooth body rotation
    const diff = targetYaw - smoothedYaw.current;
    const normalizedDiff = Math.atan2(Math.sin(diff), Math.cos(diff));
    if (Math.abs(normalizedDiff) > 0.05) {
      smoothedYaw.current += normalizedDiff * 0.2;
    }
    bodyRef.current.rotation.y = smoothedYaw.current;

    // 3. Position avatar at camera's local XZ, nudged forward 0.15m
    //    along the body's facing direction so the camera ends up
    //    inside the avatar's head rather than behind it.
    const bodyYaw = smoothedYaw.current;
    const offsetX = -Math.sin(bodyYaw) * 0.15;
    const offsetZ = -Math.cos(bodyYaw) * 0.15;
    bodyRef.current.position.set(
      cam.position.x + offsetX,
      -vrLift.applied, // seated mode lifts the rig; keep feet on the floor
      cam.position.z + offsetZ
    );
  });

  if (!isPresenting) return null;

  return avatarUrl ? (
    <Suspense fallback={<FallbackBody bodyRef={bodyRef} />}>
      {isVrmAvatar(avatarUrl, storeAnims)
        ? <VrmVRBody key={avatarUrl} url={avatarUrl} idleUrl={typeof storeAnims?.idle === 'string' ? storeAnims.idle : null} bodyRef={bodyRef} />
        : <RPMBody url={avatarUrl} bodyRef={bodyRef} />}
    </Suspense>
  ) : (
    <FallbackBody bodyRef={bodyRef} />
  );
};

export default VRAvatarBody;
